import { createApi } from './axios';
import { usePlatformAuth, PlatformAdmin } from '../stores/auth.platform';
import type { PlanDisplayFields } from '../pages/platform/plan-display-form';

const api = createApi(
  () => usePlatformAuth.getState().jwt,
  () => usePlatformAuth.getState().logout(),
);

export interface Tenant {
  id: string;
  tenant_type: 'personal' | 'enterprise' | 'group' | 'government';
  parent_tenant_id?: string | null;
  name: string;
  status: 'active' | 'suspended' | 'closed';
  created_at: string;
  // 企业资料扩展字段（getTenant 详情返回；列表里可能为空/不存在）
  credit_code?: string | null;
  industry?: string | null;
  scale?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  business_scope?: string | null;
  address?: string | null;
  website?: string | null;
  logo_url?: string | null;
  company_brief?: string | null;
  // 运营字段（A 端补的）
  plan_id?: string | null;       // 当前订阅套餐 id
  plan_name?: string | null;     // 套餐名称（denormalized 便于列表展示）
  sales_owner?: string | null;   // 销售归属（姓名/工号）
  remark?: string | null;        // 备注
}

/**
 * 氚云（H3Yun）开放平台凭据绑定状态 —— **不含 Secret 明文**，
 * 只有掩码 `secret_hint`（形如 ****ab12；短密钥为 ****）。
 */
export interface H3yunCredential {
  engine_code: string | null;
  secret_set: boolean;
  secret_hint: string | null;
  bound_at: string | null;
}

/** 工作台 Tab 可见成员摘要。 */
export interface WorkbenchTabViewer {
  membership_id: string;
  user_id: string;
  /** 展示名（名片真名优先，与用户列表「姓名」同口径）。 */
  nickname: string | null;
  /** 展示手机（名片手机优先）。 */
  phone: string | null;
}

/** 工作台 Tab（服务端返回的单条，带 id 与顺序）。 */
export interface WorkbenchTab {
  id: string;
  /** 槽位字母（A/B/C…）：行的稳定编号，跟随行、不随排序变化。 */
  slot: string;
  name: string;
  visible: boolean;
  /** 展示顺序，0 起（与数组下标一致）。 */
  sort_order: number;
  /** 勾选的图表应用模块编码。 */
  module_codes: string[];
  viewers: WorkbenchTabViewer[];
}

/** 提交给后端的工作台 Tab（无 id —— 整表覆盖，服务端按 slot upsert）。 */
export interface WorkbenchTabInput {
  /** 槽位字母；不传则由后端补最小空缺字母（弹窗始终会带上）。 */
  slot?: string;
  name: string;
  visible: boolean;
  module_codes?: string[];
  viewer_membership_ids?: string[];
}

/** 工作台 Tab 可勾选的图表应用模块（与后端 workbench-chart-modules 对齐）。 */
export const WORKBENCH_CHART_MODULE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'business_school', label: '商学院' },
  { value: 'hr_admin', label: '人事行政' },
  { value: 'promo', label: '品牌宣传' },
  { value: 'market', label: '市场部' },
  { value: 'market_daily', label: '市场日报' },
  { value: 'expense', label: '费控报销' },
  { value: 'project_rd', label: '产品研发' },
  { value: 'investment', label: '招商' },
  { value: 'task', label: '任务督办' },
];

/** 租户内可重置密码的管理员（owner / admin）— listTenantAdmins 返回元素。*/
export interface TenantAdmin {
  id: string;
  phone: string | null;
  nickname: string | null;
  role: 'tenant_owner' | 'tenant_admin';
  created_at: string;
}

export interface EnterpriseShopSummary {
  shop_id: string;
  shop_name: string;
  status: 'active' | 'suspended' | 'unbound';
  visible: boolean;
  inherited_from_tenant_id?: string;
  inherited_from_tenant_name?: string;
  can_manage_visibility?: boolean;
}

export type KingkongFeatureKey = 'smart_assistant' | 'smart_customer_service' | 'geo_matrix' | 'enterprise_shop';
export interface KingkongFeatureState {
  enabled: boolean;
  local_enabled: boolean;
  platform_granted: boolean;
  inherited: boolean;
  inherited_from_tenant_id: string | null;
  inherited_from_tenant_name: string | null;
  can_manage: boolean;
}
export interface KingkongFeatureAccess {
  eligible: boolean;
  tenant_id: string;
  tenant_type: string;
  features: Record<KingkongFeatureKey, KingkongFeatureState>;
}

/** API 错误响应统一形状（NestJS ValidationPipe + class-validator 标准）。*/
export interface ApiErrorBody {
  message?: string | string[];
  error?: string;
  /** 原始后端机器码 —— axios 拦截器把 error 改写成中文前保留到这里，供按码分支用。 */
  error_code?: string;
  statusCode?: number;
}

// Order types and API
export interface Order {
  id: string;
  order_no: string;       // 人类可读订单编号 YR{YYYYMMDD}{6随机}（导出/对账/客服找单）
  user_id: string;
  tenant_id: string | null;
  plan_id: string | null;
  product_type: string;
  // 元睿商城订单：商品 id + 名称快照（product_type='mall_product' 时非空）
  mall_product_id?: string | null;
  mall_product_name?: string | null;
  amount_yuan: string | number;
  // ADR-40 代充值 / ADR-49 企业元力池充值：精确到账元力数（不按 rmb_per_coin 折算、不计促销）
  credit_coins?: string | number | null;
  // ADR-41 升级折抵明细（套餐升级订单非空；amount_yuan 仍为实付）
  original_amount_yuan?: string | number | null;
  discount_yuan?: string | number | null;
  discount_kind?: string | null;
  payment_channel: string;
  status: 'pending' | 'paid' | 'refunded' | 'cancelled';
  paid_at: string | null;
  created_at: string;
  wx_transaction_id?: string | null;
  invoice_request_id?: string | null;
  // 对公转账专属
  bank_transfer_voucher_url?: string | null;
  reconciliation_status?: 'pending' | 'matched' | 'mismatch' | 'abandoned' | string;
  reconciled_at?: string | null;
  reconcile_remark?: string | null;
  // 关联展开（admin list/detail 才会带，miniapp/tenant 接口不一定）
  user?: { id: string; phone: string | null; nickname: string | null } | null;
  tenant?: { id: string; name: string; tenant_type?: string; contact_name: string | null; contact_phone: string | null } | null;
  plan?: { id: string; code: string; name: string; applicable_tenant_type: string } | null;
  invoice_request?: {
    id: string;
    status: string;
    invoice_title: string;
    amount_yuan: string | number;
    created_at: string;
    resolved_at: string | null;
  } | null;
}

// Plan extended (add fields beyond what platformApi already has)
export interface Plan extends PlanDisplayFields {
  id: string;
  code: string;
  name: string;
  applicable_tenant_type: 'personal' | 'enterprise' | 'group' | 'government';
  price_yuan: number;
  total_seats: number;
  max_subsidiaries?: number;
  grant_coins?: number; // ADR-38：开通赠送元力（每次套餐激活一次性赠给租户 owner）
  duration_days?: number;
  audit_level_options?: string[];
  ai_features?: {
    daily_brief_per_day: number;
    weekly_report_enabled: boolean;
    execute_code_per_day: number;
    image_gen_per_day: number;
    proactive_reminders_enabled: boolean;
    chat_high_enabled: boolean;
  };
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Tenant relation (admin read-only view)
export interface AdminTenantRelation {
  id: string;
  group_id: string;
  enterprise_id: string;
  status: 'pending' | 'active' | 'dissolving' | 'dissolved';
  initiated_by: string;
  bound_at: string | null;
  dissolve_at: string | null;
  hard_dissolve_at: string | null;
  rejected_reason: string | null;
  created_at?: string;
  group?: { id: string; name: string };
  enterprise?: { id: string; name: string };
}

// Keyword alert (V1#55 + ADR-22)
export interface KeywordAlert {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  source: 'card' | 'highlight' | 'message' | 'ai_chat';
  source_id: string;
  keyword_hit: string;
  context: string;
  status: 'pending' | 'resolved';
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  tenant_name?: string | null; // 后端解析的公司名
}

// Feedback (V1#51-52) — HEAD-side legacy type, kept for orphan pages
export interface Feedback {
  id: string;
  user_id: string;
  user_phone: string;
  user_nickname: string | null;
  content: string;
  status: 'open' | 'replied';
  reply: string | null;
  replied_by: string | null;
  replied_at: string | null;
  created_at: string;
}

// Pricing Rule — combined; 2edb6a6 wins on field shape
export interface PricingRule {
  id: string;
  skill: string;
  unit: 'token' | 'second' | 'character' | string;
  unit_price: string;
  multiplier?: string; // 每技能倍率（>0，默认 1）：按量消耗 = 用量×单价×倍率；仅按量技能生效
  cost_floor_rmb?: string | null; // 真实 API 成本（¥/单位）；成本红线告警用，null=未设
  active: boolean;
  updated_at: string;
}

// System Module Config (V1#49) — HEAD legacy
export interface SystemModuleConfig {
  module_key: string;
  default_intro_text: string;
}

// Invoice
export interface InvoiceRequest {
  id: string;
  user_id: string;
  tenant_id?: string | null;
  tenant_name?: string | null;
  user_nickname?: string | null;
  user_phone?: string | null;
  invoice_title: string;
  tax_id: string | null;
  email?: string | null;
  amount_yuan: string | number;
  /** 契约 §五：'processing'（待开票）| 'completed'（已开票）| 'rejected'（已驳回） */
  status: string;
  /** completed 时由 resolve 上传：M 端弹框渲染的图片版本 / PDF 下载地址 */
  invoice_image_url?: string | null;
  invoice_file_url?: string | null;
  /** 用户申请自填备注（旧 rejected 数据曾被驳回原因覆盖） */
  remark: string | null;
  /** 财务驳回原因（rejected 时必有，与用户备注分列） */
  rejection_reason?: string | null;
  created_at: string;
  resolved_at: string | null;
}

// Finance dashboard
export interface FinanceDashboardResp {
  filters: {
    start_date?: string;
    end_date?: string;
    product_type?: string;
    tenant_id?: string;
  };
  summary: {
    paid_count: number;
    paid_revenue: number;
    pending_count: number;
    pending_amount: number;
  };
  by_product: Array<{
    product_type: string;
    product_label: string;
    paid_count: number;
    paid_revenue: number;
  }>;
  by_tenant: Array<{
    tenant_id: string | null;
    tenant_name: string;
    tenant_type: string | null;
    paid_count: number;
    paid_revenue: number;
  }>;
  timeline: Array<{
    date: string;
    revenue: number;
    count: number;
  }>;
  invoices: {
    by_status: Array<{
      status: string;
      count: number;
      total_amount: number;
    }>;
    totals: {
      total_count: number;
      total_amount: number;
      issued_amount: number;
    };
  };
}

// Bank transfer review (创建租户「已付费」分支自动生成)
export interface BankTransferReview {
  id: string;
  tenant_id: string;
  tenant_name: string;
  amount_yuan: string | number;
  paid_at: string | null;
  voucher_url: string; // base64 data URL 或外链
  remark: string | null;
  status: 'pending' | 'approved' | 'rejected' | string;
  /** 创建租户时同步发的首订阅（可空）。驳回时后端会精确作废这一条订阅。 */
  subscription_id: string | null;
  resolved_at: string | null;
  resolver_id: string | null;
  resolve_remark: string | null;
  created_at: string;
  updated_at: string;
}

// PendingCharge (ADR-32 post-pay 计费兜底)
// claw → /internal/billing/charge 失败 → 内联重试 → 仍失败落 pending_charges 表
// B cron 30min 自动重试; A 端在此人工干预
export interface PendingCharge {
  id: string;
  user_id: string;
  tenant_id: string;
  skill: string;
  message_id: string;
  coins: number;
  trial_split: number;
  paid_split: number;
  retry_count: number;
  status: 'pending' | 'resolved' | 'abandoned';
  last_error: string | null;
  created_at: string;
  updated_at: string;
  tenant_name?: string | null;
  user_nickname?: string | null;
  user_phone?: string | null;
}

// === Phase 5c types ===

// 与 admin-tenant/src/api/tenant.ts 的 AiUsageRow 同构（后端 ai-usage rows 契约）——改字段两端同步（review C9）
export interface AiUsageRow {
  key: string;
  label?: string | null; // 按租户维度时后端解析的公司名（个人订单为「个人（无租户）」）
  trial_coins: string;
  paid_coins: string;
  total_coins: string;
  call_count: number;
}

export interface AiUsageResponse {
  group_by: 'day' | 'skill' | 'tenant';
  rows: AiUsageRow[];
}

// ADR-26 修订：审计等级只剩 off / full_audit（keyword_alert 档位已废除）
export interface TenantAuditConfigRow {
  tenant_id: string;
  level: 'off' | 'full_audit';
  updated_at: string | null;
  tenant?: { id: string; name: string; tenant_type: string };
}

export interface MessagesArchiveItem {
  id: string;
  tenant_id: string;
  user_id: string;
  session_id: string | null;
  role: 'user' | 'assistant' | 'system';
  content: string;
  skill: string;
  metadata: Record<string, any>;
  created_at: string;
  tenant_name?: string | null;
  user_nickname?: string | null;
  user_phone?: string | null;
}

export interface MessagesArchiveResponse {
  items: MessagesArchiveItem[];
  total: number;
  limit: number;
  offset: number;
}

// 元睿商城商品（平台全局；价格 Decimal 出参为字符串，编辑提交前 Number() 强转）
export interface MallProduct {
  id: string;
  name: string;
  price_yuan: string | number;
  original_price_yuan: string | number | null;
  cover_url: string;
  swiper_urls: string[];
  detail_image_url: string | null; // 旧单张（兼容），权威为 detail_image_urls
  detail_image_urls: string[];
  status: 'on' | 'off';
  display_order: number;
  sold_count?: number; // 真实成交订单数（列表只读，创建/编辑不提交）
  created_at: string;
  updated_at: string;
}

export interface RechargePromotion {
  id: string;
  name: string;
  description: string | null;
  applies_to_role: string | null;
  min_recharge_yuan: number;
  bonus_type: 'percent' | 'fixed_coins';
  bonus_value: number;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

// === Phase 3c types ===

export interface CmsArticle {
  id: string;
  category: 'help' | 'faq' | 'announcement';
  slug: string;
  title: string;
  body_md: string;
  status: 'draft' | 'published';
  created_at: string;
  updated_at: string;
}

export type LegalDocType = 'tos' | 'privacy_policy' | 'about_us' | 'voiceprint_agreement';

export interface LegalDoc {
  id: string;
  doc_type: LegalDocType;
  version: string;
  body_md: string;
  effective_at: string;
  created_at: string;
}

export interface UserFeedback {
  id: string;
  user_id: string | null;
  tenant_id: string | null;
  body: string;
  image_urls: string[];
  status: 'open' | 'resolved' | 'closed';
  reply: string | null;
  replied_by: string | null;
  created_at: string;
  replied_at: string | null;
  tenant_name?: string | null;
  user_nickname?: string | null;
  user_phone?: string | null;
}

/** 名片模版：M 端名片背景图库行 */
export interface CardTemplateRow {
  id: string;
  style: 'dark' | 'light';
  name: string | null;
  image_url: string;
  source: 'platform' | 'tenant';
  tenant_id?: string | null;
  display_order: number;
  active: boolean;
  /** 全局默认（全平台唯一）：未设置主题的用户/企业取它的色系+背景 */
  is_default: boolean;
  created_at: string;
}

export interface IndividualUser {
  id: string;
  tenant_id: string;
  nickname: string | null;
  avatar_url: string | null;
  phone: string | null;
  /** 半注册判据一半（ADR-53）：有 openid 无 phone = 微信登录了但没完成手机号授权 */
  wx_openid?: string | null;
  email: string | null;
  username: string | null;
  role: string;
  created_at: string;
  last_login_at: string | null;
  // 列表 join **本行身份的名片**得到（该身份无名片时 null + has_card=false）
  company: string | null;
  // 本行身份名片的真名（姓名列优先显示，空则回退 nickname）
  name_zh: string | null;
  // 本行身份名片的手机号（手机号列优先显示，空则回退登录手机号 phone）
  card_phone?: string | null;
  has_card: boolean;
  // ── 一身份一行：以下三项描述的是**本行这个身份**，不是「用户注册时的租户」──
  // 行唯一键：同一 user 可占多行（个人 + 企业），rowKey 必须用它
  membership_id?: string;
  joined_at?: string | null;
  tenant_type: 'personal' | 'enterprise' | 'group' | 'government' | null;
  tenant_name: string | null;
  /** 该用户共有几个身份（≥2 时列表打「多身份」角标；冻结/充值弹框据此写明影响范围） */
  identity_count?: number;
  identities?: Array<{ tenant_name: string; tenant_type: string; role: string }>;
  // ADR-39 账户冻结：null=正常，非 null=已冻结（冻结时间戳）
  frozen_at: string | null;
  frozen_reason: string | null;
  // 会员状态（仅个人用户 personal 有值）：active=已开通 / inactive=未开通；非个人租户=null（不适用）
  membership_status: 'active' | 'inactive' | null;
  // 会员到期时间（个人已开通时非 null）
  membership_expires_at: string | null;
  // 会员来源（active 时非 null）：admin_grant=超管免费开通（可关闭收回）/ paid=真实付费（不可关）
  membership_source: 'admin_grant' | 'paid' | null;
  /**
   * 首页「元睿商城」栏目是否展示。**仅免费开通(admin_grant)的会员有值**，
   * 其余为 null = 该行不出这个开关（付费会员的首页归他自己在 M 端做主，运营不越权改）。
   */
  show_yuanrui_mall: boolean | null;
  /**
   * 注册来源：share=通过分享链接进入（?ref 绑定过邀请人，ADR-33）/
   * search=自然进入（无邀请人）。挂在 User 上，同人多身份行同值。
   */
  source: 'share' | 'search';
}

export interface IndividualUserDetail {
  user: IndividualUser;
  account: { id: string; balance_coins: string; trial_balance: string } | null;
  order_count: number;
}

export interface ModuleIntro {
  module_key: 'company_brief' | 'official_video' | 'highlights';
  default_intro_text: string;
  updated_at: string;
}

// miniapp 聊天「大家都在问」推荐词（运营维护）
export type ChatSuggestionIcon = 'hotspot' | 'weather';
export interface ChatSuggestionItem {
  text: string;
  icon: ChatSuggestionIcon;
}

// AI 语音音色（火山 speaker id + 显示名 + 性别；运营维护，miniapp 语音页读，claw TTS 用）
export type AiVoiceGender = 'female' | 'male';
export interface AiVoice {
  speaker_id: string;
  label: string;
  gender: AiVoiceGender;
  sub: string | null;
  avatar: string | null;
  enabled: boolean;
}

// 首页默认名片/默认视频（游客态 & 非会员无名片态展示；miniapp 走 public 端点拉同一数据）
export interface HomeDefaultCard {
  name_zh: string | null;
  title: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  avatar_url: string | null;
}
export interface HomeDefaultVideo {
  video_url: string;
  cover_url: string | null;
}
export type HomeDefaultUploadPurpose = 'home_default_avatar' | 'home_default_video' | 'home_default_video_cover' | 'staff_wechat_qr';

// ─── Audit Logs & Login Logs (V1#41-42, spec §4.8) ───
// 后端：GET /api/admin/audit-logs + /api/admin/login-logs（AuditLogsAdminController）
export type AuditCategory = 'admin_action' | 'tenant_action' | 'user_action' | 'ai_invoke_metadata';

export interface AdminAuditLog {
  id: string;
  category: AuditCategory;
  tenant_id: string | null;
  user_id: string | null;
  actor_username: string | null;   // join 后的展示用，便于前端无需二次查询
  action: string;
  resource: string;
  resource_id: string | null;
  ip: string | null;
  user_agent: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export type LoginActorType = 'platform_admin' | 'tenant_admin' | 'tenant_member' | 'individual';
export type LoginMethod = 'password' | 'sms' | 'email' | 'wechat';
export type LoginResult = 'success' | 'failed';

export interface AdminLoginLog {
  id: string;
  actor_type: LoginActorType;
  actor_id: string | null;          // 失败 + 用户名不存在时为 null
  actor_username: string;
  actor_display: string | null;     // 昵称 / 企业名 等
  login_method: LoginMethod;
  ip: string;
  user_agent: string;
  result: LoginResult;
  failure_reason: string | null;    // 'invalid_credentials' / 'account_disabled' / 'rate_limited' / ...
  created_at: string;
}

// ─── Staff & Roles & Permissions (V1#39-40, spec §4.10) ───
// 平台内部运营部门（小集合，与 tenant Department 表无关）
export const COMMON_DEPARTMENTS = [
  '运营部',
  '财务部',
  '客服部',
  '内容审核部',
  '技术部',
  '法务部',
  '其他',
] as const;

export interface PlatformStaff {
  id: string;
  username: string;
  nickname: string;
  phone: string | null;
  email: string | null;
  department: string | null;
  role_id: string;
  role: { id: string; code: string; name: string };
  status: 'active' | 'disabled';
  must_change_pw: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface CreateStaffResp {
  staff: PlatformStaff;
  initial_password_plaintext: string;
}

export interface ResetStaffPasswordResp {
  user_id: string;
  username: string;
  initial_password_plaintext: string;
}

export interface PlatformRole {
  id: string;
  code: string;
  name: string;
  description: string;
  permissions: string[];   // ['*'] = 所有权限；否则 page-level keys
  is_builtin: boolean;
  staff_count: number;
  created_at: string;
  updated_at: string;
}

export interface PermissionGroup {
  key: string;
  label: string;
  // privileged: 仅内置 super_admin 可拥有的特权权限（后端 PermissionDef 镜像，前端编辑器可据此禁勾）
  perms: { key: string; label: string; privileged?: boolean }[];
}

/** 服务大厅条目（A 端运营维护 → M 端「服务大厅」tab 展示）。created_at 即列表「发布时间」。 */
export interface ServiceHallItem {
  id: string;
  title: string;
  summary: string | null;
  /** 多个关键词用英文逗号分隔（端上拆标签展示） */
  keywords: string | null;
  icon_url: string | null;
  is_hot: boolean;
  link_url: string | null;
  service_phone: string | null;
  /** 展示排序序号，越小越靠前 */
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const platformApi = {
  login: (username: string, password: string) =>
    api.post<{ jwt: string; user: PlatformAdmin; permissions: string[] }>('/admin/auth/login', { username, password }).then(r => r.data),

  // TODO(backend): 后端尚未实现，详见 docs/plans/admin-platform-auth-multi-method-stub.md
  requestSmsCode: (phone: string) =>
    api.post<{ sent: true; ttl: number }>('/admin/auth/sms-code', { phone }).then(r => r.data),
  requestEmailCode: (email: string) =>
    api.post<{ sent: true; ttl: number }>('/admin/auth/email-code', { email }).then(r => r.data),
  loginBySms: (phone: string, code: string) =>
    api.post<{ jwt: string; user: PlatformAdmin; permissions: string[] }>('/admin/auth/login-sms', { phone, code }).then(r => r.data),
  loginByEmail: (email: string, code: string) =>
    api.post<{ jwt: string; user: PlatformAdmin; permissions: string[] }>('/admin/auth/login-email', { email, code }).then(r => r.data),

  me: () => api.get<PlatformAdmin & { permissions: string[] }>('/admin/me').then(r => r.data),
  listTenants: (params: { page?: number; page_size?: number; tenant_type?: string; status?: string; search?: string }) =>
    api.get<{ items: Tenant[]; total: number; page: number; page_size: number }>('/admin/tenants', { params }).then(r => r.data),
  enterTenantBackend: (tenantId: string) =>
    api.post<{ entry_url: string; expires_at: string }>(`/admin/tenants/${tenantId}/enter-backend`).then(r => r.data),
  /**
   * 创建租户 — 支持全量企业资料 + 可选初始管理员 + 可选初始套餐订阅。
   * 后端返回 { tenant, admin?, subscription? } ——
   *  - admin.initial_password_plaintext 是一次性明文密码，前端**必须**用 PasswordRevealModal
   *    展示给 admin 复制传给本人，关闭弹窗后再也查不到（只能走 resetTenantAdminPassword 重置）。
   *  - 密码由后端 CSPRNG 生成 16 位强随机，前端不再让用户填密码字段。
   */
  createTenant: (data: {
    // 基础（必填）
    name: string;
    tenant_type: 'enterprise' | 'group' | 'government';
    parent_tenant_id?: string;
    // 企业资料（可选 9 字段）
    credit_code?: string;
    industry?: string;
    scale?: string;
    contact_name?: string;
    contact_phone?: string;
    business_scope?: string;
    address?: string;
    website?: string;
    logo_url?: string;
    // 初始管理员（可选）— 填了 phone 后端自动生成密码
    admin_phone?: string;
    admin_nickname?: string;
    // owner 登录账号；不填后端默认 = admin_phone（E 后台仅按 username 登录）
    admin_username?: string;
    // 初始套餐订阅（可选）
    plan_id?: string;
    // 运营字段
    sales_owner?: string;
    remark?: string;
    // 对公转账（已付费场景）— is_paid=true 时其它字段必填
    is_paid?: boolean;
    transfer_voucher_url?: string;
    transfer_amount_yuan?: number;
    transfer_paid_at?: string;
    transfer_remark?: string;
  }) =>
    api.post<{
      tenant: Tenant;
      admin: {
        id: string;
        phone: string;
        username: string;
        nickname: string | null;
        // ADR-34：手机号已是真人号时挂 owner membership 沿用现有密码 → null（不生成新密码）
        initial_password_plaintext: string | null;
        reused_existing: boolean;
      } | null;
      subscription: { id: string; plan_id: string; expires_at: string } | null;
      bank_transfer_review: { id: string; status: string } | null;
    }>('/admin/tenants', data).then(r => r.data),
  getTenant: (id: string) => api.get<Tenant>(`/admin/tenants/${id}`).then(r => r.data),
  listEnterpriseShopCatalog: () => api.get<{ items: { shop_id: string; shop_name: string; status?: string }[] }>('/admin/enterprise-shops/catalog').then(r => r.data),
  getEnterpriseShop: (tenantId: string) => api.get<EnterpriseShopSummary | null>(`/admin/enterprise-shops/tenants/${tenantId}`).then(r => r.data),
  getKingkongFeatures: (tenantId: string) => api.get<KingkongFeatureAccess>(`/admin/enterprise-shops/tenants/${tenantId}/kingkong-features`).then(r => r.data),
  setKingkongFeatureGrant: (tenantId: string, featureKey: KingkongFeatureKey, granted: boolean) =>
    api.patch(`/admin/enterprise-shops/tenants/${tenantId}/kingkong-features/${featureKey}/grant`, { granted }).then(r => r.data),
  bindEnterpriseShop: (tenantId: string, data: { shop_id: string }) => api.put(`/admin/enterprise-shops/tenants/${tenantId}/binding`, data).then(r => r.data),
  setEnterpriseShopStatus: (tenantId: string, status: 'active' | 'suspended') => api.patch(`/admin/enterprise-shops/tenants/${tenantId}/status`, { status }).then(r => r.data),
  unbindEnterpriseShop: (tenantId: string) => api.post(`/admin/enterprise-shops/tenants/${tenantId}/unbind`).then(r => r.data),
  updateTenant: (
    id: string,
    data: {
      name?: string;
      status?: 'active' | 'suspended' | 'closed';
      credit_code?: string;
      industry?: string;
      scale?: string;
      contact_name?: string;
      contact_phone?: string;
      business_scope?: string;
      address?: string;
      website?: string;
      logo_url?: string;
      company_brief?: string;
      plan_id?: string | null;
      sales_owner?: string;
      remark?: string;
    },
  ) => api.patch<Tenant>(`/admin/tenants/${id}`, data).then(r => r.data),
  /**
   * 列租户内可重置密码的管理员（owner / admin）。
   * 前端"重置密码"操作先调这个 → 让 admin 挑人 → 再 POST reset-password。
   */
  listTenantAdmins: (tenantId: string) =>
    api.get<TenantAdmin[]>(`/admin/tenants/${tenantId}/admins`).then(r => r.data),
  /**
   * 重置租户内 owner / admin 的密码 — 后端自动生成新强密码，
   * 返回 initial_password_plaintext 一次性给前端弹窗显示。
   * 关闭弹窗后再也查不到，需要再重置就再调一次。
   */
  resetTenantAdminPassword: (tenantId: string, userId: string) =>
    api.post<{
      user_id: string;
      phone: string | null;
      nickname: string | null;
      initial_password_plaintext: string;
    }>(`/admin/tenants/${tenantId}/admins/${userId}/reset-password`).then(r => r.data),

  // ===== 氚云（H3Yun）开放平台凭据 =====
  // Secret 后端明文落库但**永不回显明文**，只回 secret_hint 掩码（如 ****ab12）。

  /** 读取绑定状态 —— 「绑定氚云秘钥」弹窗打开时回填 EngineCode + 掩码。 */
  getTenantH3yunCredential: (tenantId: string) =>
    api.get<H3yunCredential>(`/admin/tenants/${tenantId}/h3yun-credential`).then(r => r.data),
  /** 绑定 / 覆盖氚云秘钥（EngineCode + Secret）。重复提交即覆盖并刷新绑定时间。 */
  bindTenantH3yunCredential: (tenantId: string, data: { engine_code: string; secret: string }) =>
    api.put<H3yunCredential>(`/admin/tenants/${tenantId}/h3yun-credential`, data).then(r => r.data),
  /** 解除绑定（幂等：未绑定时也返回空状态，不报错）。 */
  unbindTenantH3yunCredential: (tenantId: string) =>
    api.delete<H3yunCredential>(`/admin/tenants/${tenantId}/h3yun-credential`).then(r => r.data),

  // ===== 工作台 Tab 配置 =====
  // 名称 + 显隐 + 顺序 + 图表模块 + 可见成员。

  /** 读取工作台 Tab 列表（按展示顺序）。 */
  listTenantWorkbenchTabs: (tenantId: string) =>
    api.get<{ items: WorkbenchTab[] }>(`/admin/tenants/${tenantId}/workbench-tabs`).then(r => r.data),
  /** 可见成员候选（active membership）。 */
  listTenantWorkbenchTabMembers: (tenantId: string, search?: string) =>
    api
      .get<{ items: WorkbenchTabViewer[] }>(`/admin/tenants/${tenantId}/workbench-tab-members`, {
        params: search?.trim() ? { search: search.trim() } : undefined,
      })
      .then((r) => r.data),
  /**
   * 保存工作台 Tab 配置 —— **整表覆盖**，数组顺序即展示顺序；
   * 传 `[]` 表示清空。名称重复 / 超过 20 条 / 已注销租户会被后端 400。
   */
  saveTenantWorkbenchTabs: (tenantId: string, tabs: WorkbenchTabInput[]) =>
    api.put<{ items: WorkbenchTab[] }>(`/admin/tenants/${tenantId}/workbench-tabs`, { tabs }).then(r => r.data),

  listOrders: (params: {
    page?: number;
    page_size?: number;
    status?: string;
    product_type?: string;
    tenant_id?: string;
    search?: string;   // 手机号 / 姓名 / 订单号 模糊匹配（按人查账，覆盖个人订单）
    start_at?: string; // ISO；created_at >=
    end_at?: string;   // ISO；created_at <=
  }) =>
    api.get<{ items: Order[]; total: number; page: number; page_size: number }>('/admin/orders', { params }).then(r => r.data),
  getOrder: (id: string) => api.get<Order>(`/admin/orders/${id}`).then(r => r.data),
  // 退款入口 v1 暂不提供：后端无 /refund 端点及资金/订阅反向处置逻辑（mock 支付阶段无真实资金流）。
  // 待微信支付真接入时连同原路退款一起设计，再恢复 UI。refunded 状态枚举/财务统计保留。
  reconcileOrder: (id: string, payload: { status: 'pending' | 'matched' | 'mismatch' | 'abandoned'; remark?: string }) =>
    api.post<Order>(`/admin/orders/${id}/reconcile`, payload).then(r => r.data),
  /** 对公转账「确认到账」：人工结算 pending 订单（充值入账/套餐激活），E 代充值的唯一到账路径 */
  confirmTransferOrder: (id: string) =>
    api.post<Order>(`/admin/orders/${id}/confirm-transfer`).then(r => r.data),

  listPlans: (includeInactive?: boolean) =>
    api.get<Plan[]>('/admin/plans', { params: { include_inactive: includeInactive ? 'true' : 'false' } }).then(r => r.data),
  getPlan: (id: string) => api.get<Plan>(`/admin/plans/${id}`).then(r => r.data),
  getPlanDisplayAssets: () => api.get<Array<{ value: string; label: string; url: string }>>('/admin/plans/display-assets').then(r => r.data),
  createPlan: (data: Partial<Plan>) => api.post<Plan>('/admin/plans', data).then(r => r.data),
  updatePlan: (id: string, data: Partial<Plan>) => api.patch<Plan>(`/admin/plans/${id}`, data).then(r => r.data),
  deactivatePlan: (id: string) => api.delete<Plan>(`/admin/plans/${id}`).then(r => r.data),

  listInvoices: (params: { page?: number; page_size?: number; status?: string }) =>
    api.get<{ items: InvoiceRequest[]; total: number; page: number; page_size: number }>('/admin/invoices', { params }).then(r => r.data),
  // 开具发票：后端置 completed + 回填发票文件地址（契约 §五，DTO 无 status 字段）
  resolveInvoice: (id: string, payload: { invoice_file_url?: string; invoice_image_url?: string }) =>
    api.post<InvoiceRequest>(`/admin/invoices/${id}/resolve`, payload).then(r => r.data),
  // 驳回发票申请：rejected 终态 + 订单解锁可重新申请，原因必填（落 rejection_reason + inbox 通知用户）
  rejectInvoice: (id: string, reason: string) =>
    api.post<InvoiceRequest>(`/admin/invoices/${id}/reject`, { reason }).then(r => r.data),
  // 发票图片/PDF 直传 — 走 OSS，后端 purpose=invoice_file（图片/PDF，5MB 上限）
  uploadInvoiceFile: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=invoice_file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  // ── 名片模版：系统推荐库管理（写 admin-only）──
  listCardTemplates: (style?: 'dark' | 'light') =>
    api.get<CardTemplateRow[]>('/admin/card-templates', { params: style ? { style } : {} }).then(r => r.data),
  createCardTemplate: (data: { style: 'dark' | 'light'; image_url: string; name?: string; display_order?: number }) =>
    api.post<CardTemplateRow>('/admin/card-templates', data).then(r => r.data),
  updateCardTemplate: (id: string, data: { image_url?: string; name?: string; display_order?: number; active?: boolean }) =>
    api.patch<CardTemplateRow>(`/admin/card-templates/${id}`, data).then(r => r.data),
  deleteCardTemplate: (id: string) =>
    api.delete(`/admin/card-templates/${id}`).then(r => r.data),
  /** 设为全局默认模版（全平台唯一，单选切换）*/
  setDefaultCardTemplate: (id: string) =>
    api.post<{ ok: boolean; default_id: string }>(`/admin/card-templates/${id}/set-default`).then(r => r.data),
  uploadCardTemplateImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=card_template', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  // 元睿商城商品管理（平台全局，M 端首页「元睿商城」栏目数据源；写操作 admin-only）
  listMallProducts: (params?: { page?: number; page_size?: number }) =>
    api.get<{ items: MallProduct[]; total: number; page: number; page_size: number }>('/admin/mall-products', { params }).then(r => r.data),
  createMallProduct: (data: Partial<MallProduct>) =>
    api.post<MallProduct>('/admin/mall-products', data).then(r => r.data),
  updateMallProduct: (id: string, data: Partial<MallProduct>) =>
    api.put<MallProduct>(`/admin/mall-products/${id}`, data).then(r => r.data),
  toggleMallProduct: (id: string) =>
    api.post<MallProduct>(`/admin/mall-products/${id}/toggle-status`).then(r => r.data),
  deleteMallProduct: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/mall-products/${id}`).then(r => r.data),
  // 商品图上传（封面/轮播/详情长图；purpose=mall_image，图片 ≤10MB）
  uploadMallImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=mall_image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  // Bank transfer review (创建租户时勾选「已付费」后自动生成 pending 行)
  listBankTransferReviews: (params: { page?: number; page_size?: number; status?: string; tenant_id?: string }) =>
    api.get<{ items: BankTransferReview[]; total: number; page: number; page_size: number }>('/admin/bank-transfer-reviews', { params }).then(r => r.data),
  resolveBankTransferReview: (id: string, payload: { status: 'approved' | 'rejected'; resolve_remark?: string }) =>
    api.post<BankTransferReview>(`/admin/bank-transfer-reviews/${id}/resolve`, payload).then(r => r.data),

  // Finance dashboard
  financeDashboard: (params: { start_date?: string; end_date?: string; product_type?: string; tenant_id?: string }) =>
    api.get<FinanceDashboardResp>('/admin/finance/dashboard', { params }).then(r => r.data),

  // ADR-32 — Pending charges（post-pay 计费兜底，A 端人工重试 / 放弃）
  listPendingCharges: (params: { status?: 'pending' | 'resolved' | 'abandoned'; user_id?: string; skill?: string; limit?: number; offset?: number }) =>
    api.get<{ items: PendingCharge[]; total: number }>('/admin/pending-charges', { params }).then(r => r.data),
  retryPendingCharge: (id: string) =>
    api.post<{ ok: boolean; id: string; status: string; billed?: number; error?: string }>(`/admin/pending-charges/${id}/retry`).then(r => r.data),
  /** 放弃 charge — 后端 @Roles('platform_admin')，operator 调用会 403。 */
  abandonPendingCharge: (id: string) =>
    api.post<{ ok: boolean; id: string; status: string }>(`/admin/pending-charges/${id}/abandon`).then(r => r.data),

  listTenantRelations: (params?: { status?: string; search?: string; group_id?: string }) =>
    api.get<AdminTenantRelation[]>('/admin/tenant-relations', {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.group_id ? { group_id: params.group_id } : {}),
      },
    }).then(r => r.data),

  // Keyword alerts (V1#55)
  listKeywordAlerts: (params: { status?: 'pending' | 'resolved'; source?: string; page?: number; page_size?: number }) =>
    api.get<{ items: KeywordAlert[]; total: number; page: number; page_size: number }>('/admin/keyword-alerts', { params }).then(r => r.data),
  unresolvedKeywordAlertCount: () =>
    api.get<{ count: number }>('/admin/keyword-alerts/unresolved-count').then(r => r.data),
  resolveKeywordAlert: (id: string, note?: string) =>
    api.post<KeywordAlert>(`/admin/keyword-alerts/${id}/resolve`, { note }).then(r => r.data),
  resolveKeywordAlertAsFalse: (id: string, note?: string) =>
    api.post<KeywordAlert>(`/admin/keyword-alerts/${id}/resolve-as-false`, { note }).then(r => r.data),

  // ADR-22 强制下架 highlights
  forceHideHighlight: (id: string, reason: string) =>
    api.post<{ id: string; status: string; forced_hidden_reason: string | null }>(`/admin/highlights/${id}/force-hide`, { reason }).then(r => r.data),
  forceUnhideHighlight: (id: string) =>
    api.post<{ id: string; status: string; forced_hidden_reason: string | null }>(`/admin/highlights/${id}/force-unhide`).then(r => r.data),

  // Phase 5c — AI usage dashboard (T5)
  aiUsage: (q: { start_date?: string; end_date?: string; tenant_id?: string; user_id?: string; skill?: string; group_by?: 'day' | 'skill' | 'tenant' }) =>
    api.get<AiUsageResponse>('/admin/ai-usage', { params: q }).then(r => r.data),
  /** rollup 按日补跑（cron 漏跑补偿，幂等；仅限昨天及更早） */
  backfillAiUsage: (date: string, end_date?: string) =>
    api.post<{ days: number; results: Array<{ date: string; written: number }> }>('/admin/ai-usage/backfill', { date, end_date }).then(r => r.data),

  // ADR-26 修订 — 租户审计配置（完整对话存档开关；关键词告警已平台级全量开启，不在此页管）
  listAuditConfigs: () =>
    api.get<TenantAuditConfigRow[]>('/admin/audit-config').then(r => r.data),
  getTenantAuditConfig: (tenantId: string) =>
    api.get<{ tenant_id: string; level: 'off' | 'full_audit'; updated_at: string | null }>(`/admin/audit-config/${tenantId}`).then(r => r.data),
  overrideAuditConfig: (tenantId: string, level: 'off' | 'full_audit', reason: string) =>
    api.patch<TenantAuditConfigRow>(`/admin/audit-config/${tenantId}`, { level, reason }).then(r => r.data),

  // Phase 5c — Messages archive (T3)
  queryMessagesArchive: (q: { tenant_id?: string; user_id?: string; skill?: string; q?: string; start_at?: string; end_at?: string; limit?: number; offset?: number }) =>
    api.get<MessagesArchiveResponse>('/admin/messages-archive', { params: q }).then(r => r.data),

  // Phase 5c — Recharge promotions (T6)
  listPromotions: (active_only?: boolean) =>
    api.get<RechargePromotion[]>('/admin/recharge-promotions', { params: { active_only } }).then(r => r.data),
  createPromotion: (data: Partial<RechargePromotion>) =>
    api.post<RechargePromotion>('/admin/recharge-promotions', data).then(r => r.data),
  updatePromotion: (id: string, data: Partial<RechargePromotion>) =>
    api.patch<RechargePromotion>(`/admin/recharge-promotions/${id}`, data).then(r => r.data),
  deactivatePromotion: (id: string) =>
    api.delete<RechargePromotion>(`/admin/recharge-promotions/${id}`).then(r => r.data),

  // Phase 3c — CMS
  listCmsArticles: (q: { category?: string; status?: string; search?: string }) =>
    api.get<CmsArticle[]>('/admin/cms', { params: q }).then(r => r.data),
  getCmsArticle: (id: string) =>
    api.get<CmsArticle>(`/admin/cms/${id}`).then(r => r.data),
  createCmsArticle: (data: Partial<CmsArticle>) =>
    api.post<CmsArticle>('/admin/cms', data).then(r => r.data),
  updateCmsArticle: (id: string, data: Partial<CmsArticle>) =>
    api.patch<CmsArticle>(`/admin/cms/${id}`, data).then(r => r.data),
  deleteCmsArticle: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/cms/${id}`).then(r => r.data),

  // Phase 3c — LegalDocs（隐私协议 / 用户协议 / 关于我们 三类共表）
  listLegalDocs: (doc_type?: LegalDocType) =>
    api.get<LegalDoc[]>('/admin/legal-docs', { params: doc_type ? { doc_type } : {} }).then(r => r.data),
  createLegalDoc: (data: { doc_type: LegalDocType; version: string; body_md: string; effective_at: string }) =>
    api.post<LegalDoc>('/admin/legal-docs', data).then(r => r.data),
  updateLegalDoc: (id: string, data: { body_md?: string; effective_at?: string }) =>
    api.patch<LegalDoc>(`/admin/legal-docs/${id}`, data).then(r => r.data),

  // Phase 3c — Feedback
  listFeedbacks: (q: { status?: string; tenant_id?: string }) =>
    api.get<UserFeedback[]>('/admin/feedbacks', { params: q }).then(r => r.data),
  replyFeedback: (id: string, data: { reply: string; status?: 'resolved' | 'closed' }) =>
    api.post<UserFeedback>(`/admin/feedbacks/${id}/reply`, data).then(r => r.data),

  // Phase 3c — IndividualUsers
  // membership_source：admin_grant=免费开通 / paid=付费开通 / none=未开通；
  // 服务端按「活跃订阅 starts_at 是否命中 admin_grant 0 元单」解析成租户集合再筛（非 DB 列）
  // registration（ADR-53）：complete=已完成注册（默认口径，排除「微信登录未绑手机号」半注册）/
  // incomplete=仅半注册；缺省=全部
  listIndividualUsers: (q: { page?: number; page_size?: number; search?: string; tenant_type?: string; company?: string; freeze_status?: string; membership_source?: string; source?: string; registration?: string; created_from?: string; created_to?: string }) =>
    api.get<{ items: IndividualUser[]; total: number; page: number; page_size: number }>('/admin/individual-users', { params: q }).then(r => r.data),
  // 导出当前筛选全量 xlsx（后端上限 5000 行；参数与列表完全同一套）
  exportIndividualUsers: (q: { search?: string; tenant_type?: string; company?: string; freeze_status?: string; membership_source?: string; source?: string; registration?: string; created_from?: string; created_to?: string }) =>
    api.get<Blob>('/admin/individual-users/export.xlsx', { params: q, responseType: 'blob' }).then(r => r.data),
  // 用户构成统计：个人用户 vs 企业员工（dashboard 用）
  getUserBreakdown: () =>
    api.get<{ individual: number; enterprise_member: number; total: number }>('/admin/individual-users/stats/breakdown').then(r => r.data),
  getIndividualUser: (id: string) =>
    api.get<IndividualUserDetail>(`/admin/individual-users/${id}`).then(r => r.data),
  /**
   * 创建个人用户 — 后端自动生成 16 位强密码（仅当 phone/username 提供）。
   * 返回 initial_password_plaintext 一次性给前端用 PasswordRevealModal 展示，关闭后不可查。
   */
  createIndividualUser: (data: {
    nickname: string;
    phone?: string;
    email?: string;
    username?: string;
    initial_grant_coins?: number;
    admin_note?: string;
  }) =>
    api.post<{
      user: IndividualUser;
      tenant: { id: string; tenant_type: string; name: string };
      account: { id: string; balance_coins: string } | null;
      initial_password_plaintext: string | null;
    }>('/admin/individual-users', data).then(r => r.data),
  updateIndividualUser: (
    id: string,
    data: { nickname?: string; phone?: string; email?: string; username?: string; admin_note?: string },
  ) => api.patch<IndividualUser>(`/admin/individual-users/${id}`, data).then(r => r.data),
  /** 重置个人用户密码 — 返回明文一次性给前端弹窗显示。*/
  resetIndividualUserPassword: (id: string) =>
    api.post<{
      user_id: string;
      phone: string | null;
      nickname: string | null;
      initial_password_plaintext: string;
    }>(`/admin/individual-users/${id}/reset-password`).then(r => r.data),
  // ADR-39：冻结/解冻账户（替代硬删）
  freezeIndividualUser: (id: string, reason?: string) =>
    api.post<{ ok: boolean; frozen: boolean; frozen_at: string }>(
      `/admin/individual-users/${id}/freeze`,
      { reason },
    ).then(r => r.data),
  unfreezeIndividualUser: (id: string) =>
    api.post<{ ok: boolean; frozen: boolean }>(
      `/admin/individual-users/${id}/unfreeze`,
    ).then(r => r.data),
  getIndividualUserActionLog: (id: string) =>
    api.get<any[]>(`/admin/individual-users/${id}/action-log`).then(r => r.data),
  // 超管免费开通会员（仅个人 · 未开通 · 不付费）；时长自定义，缺省 365 天
  grantIndividualMembership: (id: string, duration_days: number) =>
    api.post<{ ok: boolean; subscription_id: string; order_id: string; expires_at: string }>(
      `/admin/individual-users/${id}/grant-membership`, { duration_days },
    ).then(r => r.data),
  // 超管关闭会员（仅限免费开通 admin_grant 的会员；订阅立即失效，付费会员 400）
  revokeIndividualMembership: (id: string) =>
    api.post<{ ok: boolean; subscription_id: string }>(
      `/admin/individual-users/${id}/revoke-membership`,
    ).then(r => r.data),
  // 超管拨该用户首页「元睿商城」栏目（仅限免费开通 admin_grant 的会员，付费会员 400 not_free_grant）。
  // 与用户在 M 端「编辑名片→首页内容」拨的是同一个开关，后写的赢。
  setIndividualMallVisibility: (id: string, show: boolean) =>
    api.post<{ ok: boolean; show_yuanrui_mall: boolean }>(
      `/admin/individual-users/${id}/mall-visibility`, { show },
    ).then(r => r.data),

  // Phase 3c — Admin grant recharge
  grantRecharge: (data: { user_id: string; amount_yuan: number; reason: string }) =>
    api.post('/admin/orders/grant-recharge', data).then(r => r.data),

  // Phase 3c — SystemConfig
  listModuleIntros: () =>
    api.get<ModuleIntro[]>('/admin/system-config/module-intros').then(r => r.data),
  upsertModuleIntro: (data: { module_key: string; default_intro_text: string }) =>
    api.put<ModuleIntro>('/admin/system-config/module-intros', data).then(r => r.data),
  getSignupBonus: () =>
    api.get<{ bonus: number }>('/admin/system-config/signup-bonus').then(r => r.data),
  // 企业亮点数量限制（V5 起 highlight-limit 语义随迁亮点域）：开=每租户新建受限；存量超限不影响
  getHighlightLimit: () =>
    api.get<{ enabled: boolean; max_count: number }>('/admin/system-config/highlight-limit').then(r => r.data),
  setHighlightLimit: (data: { enabled: boolean; max_count: number }) =>
    api.put<any>('/admin/system-config/highlight-limit', data).then(r => r.data),
  // 企业资讯（news 域）数量限制（V5 与亮点拆分独立配置）
  getNewsLimit: () =>
    api.get<{ enabled: boolean; max_count: number }>('/admin/system-config/news-limit').then(r => r.data),
  setNewsLimit: (data: { enabled: boolean; max_count: number }) =>
    api.put<any>('/admin/system-config/news-limit', data).then(r => r.data),
  setSignupBonus: (bonus: number) =>
    api.put<any>('/admin/system-config/signup-bonus', { bonus }).then(r => r.data),
  // 注册体验活动：窗口内新注册个人用户自动获赠体验会员
  getTrialCampaign: () =>
    api.get<{ enabled: boolean; start_at: string | null; end_at: string | null; duration_days: number; banner_text: string; show_yuanrui_mall: boolean }>(
      '/admin/system-config/trial-campaign',
    ).then(r => r.data),
  setTrialCampaign: (data: { enabled: boolean; start_at: string; end_at: string; duration_days?: number; banner_text?: string; show_yuanrui_mall?: boolean }) =>
    api.put<any>('/admin/system-config/trial-campaign', data).then(r => r.data),
  getInviteReward: () =>
    api.get<{ coins: number }>('/admin/system-config/invite-reward').then(r => r.data),
  setInviteReward: (coins: number) =>
    api.put<any>('/admin/system-config/invite-reward', { coins }).then(r => r.data),
  getCurrencyRate: () =>
    api.get<{ rmb_per_coin: number }>('/admin/system-config/currency-rate').then(r => r.data),
  setCurrencyRate: (rmb_per_coin: number) =>
    api.put<any>('/admin/system-config/currency-rate', { rmb_per_coin }).then(r => r.data),
  listPricingRules: () =>
    api.get<PricingRule[]>('/admin/system-config/pricing-rules').then(r => r.data),
  upsertPricingRule: (data: { skill: string; unit: string; unit_price: string; multiplier?: string; cost_floor_rmb?: string | null; active?: boolean }) =>
    api.put<PricingRule>('/admin/system-config/pricing-rules', data).then(r => r.data),
  deactivatePricingRule: (skill: string) =>
    api.delete<PricingRule>(`/admin/system-config/pricing-rules/${skill}`).then(r => r.data),
  // 全局倍率（ADR-43 cost-fold）：chat/分析 LLM 消耗 = 真实成本×global÷元力面值
  getPricingMarkup: () =>
    api.get<{ global: number }>('/admin/system-config/pricing-markup').then(r => r.data),
  setPricingMarkup: (global: number) =>
    api.put<any>('/admin/system-config/pricing-markup', { global }).then(r => r.data),

  // ─── 客服联系方式（miniapp 反馈页用 public 端点拉同一数据） ───
  getContactInfo: () =>
    api.get<{ phone: string; email: string; wechat: string }>('/admin/system-config/contact-info').then(r => r.data),
  setContactInfo: (data: { phone?: string; email?: string; wechat?: string }) =>
    api.put<{ phone: string; email: string; wechat: string }>('/admin/system-config/contact-info', data).then(r => r.data),

  // ─── 敏感词词库 ───
  getKeywordDict: () =>
    api.get<{ words: string[]; sensitive_words: string[] }>('/admin/system-config/keyword-dict').then(r => r.data),
  setKeywordDict: (data: { words: string[]; sensitive_words: string[] }) =>
    api.put<{ words: string[]; sensitive_words: string[] }>('/admin/system-config/keyword-dict', data).then(r => r.data),

  // ─── 运营：miniapp 聊天「大家都在问」推荐词（miniapp 走 public 端点拉同一数据） ───
  getChatSuggestions: () =>
    api.get<{ items: ChatSuggestionItem[] }>('/admin/system-config/chat-suggestions').then(r => r.data),
  setChatSuggestions: (items: ChatSuggestionItem[]) =>
    api.put<{ items: ChatSuggestionItem[] }>('/admin/system-config/chat-suggestions', { items }).then(r => r.data),

  // ─── 运营：AI 语音音色列表（miniapp 语音页展示；火山公版音色 ID 从控制台音色库抄） ───
  getAiVoices: () =>
    api.get<{ items: AiVoice[] }>('/admin/system-config/ai-voices').then(r => r.data),
  setAiVoices: (items: AiVoice[]) =>
    api.put<{ items: AiVoice[] }>('/admin/system-config/ai-voices', { items }).then(r => r.data),

  // ─── 运营：首页默认名片/默认视频（游客态 & 非会员无名片态展示；miniapp 走 public 端点拉） ───
  // 工作人员（客服）微信二维码：M 端购买 企业/集团/政府 会员成功后弹窗展示
  getStaffWechatQr: () =>
    api.get<{ url: string | null }>('/admin/system-config/staff-wechat-qr').then(r => r.data),
  setStaffWechatQr: (url: string) =>
    api.put<{ url: string | null }>('/admin/system-config/staff-wechat-qr', { url }).then(r => r.data),
  getHomeDefaults: () =>
    api.get<{ card: HomeDefaultCard | null; video: HomeDefaultVideo | null }>('/admin/system-config/home-defaults').then(r => r.data),
  setHomeDefaultCard: (data: Partial<Record<keyof HomeDefaultCard, string>>) =>
    api.put<HomeDefaultCard | null>('/admin/system-config/home-default-card', data).then(r => r.data),
  setHomeDefaultVideo: (data: { video_url?: string; cover_url?: string }) =>
    api.put<HomeDefaultVideo | null>('/admin/system-config/home-default-video', data).then(r => r.data),
  /**
   * 首页默认资源 STS 直传（头像/视频/封面）——浏览器直发 OSS，绕服务端 body 限制（视频可 100MB）。
   * 流程同 admin-tenant uploadToOss：sts → FormData POST（form_fields 透传、file 最后 append）→ 用服务端拼好的 public_url。
   */
  uploadHomeDefaultAsset: async (
    file: File,
    purpose: HomeDefaultUploadPurpose,
    onProgress?: (percent: number) => void,
  ): Promise<{ url: string }> => {
    const sts = await api
      .post<{ form_fields: Record<string, string>; upload_url: string; object_key: string; public_url: string; mock?: boolean }>(
        '/admin/uploads/sts',
        { purpose, size: file.size, mime: file.type || 'application/octet-stream' },
      )
      .then(r => r.data);
    if (!sts.mock) {
      const fd = new FormData();
      Object.entries(sts.form_fields ?? {}).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', file); // OSS POST Policy 要求 file 最后 append
      // XHR 不用 fetch：fetch 拿不到上传进度，
      // 视频 100MB 档下界面全程静止会被当成卡死（E 端同款问题实锤）。同步把 OSS 的 XML
      // 错误码翻成人话——原本一律「HTTP 403」，运营无从判断是超时还是配置坏了。
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', sts.upload_url, true);
        xhr.upload.onprogress = e => {
          if (e.lengthComputable && onProgress) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress?.(100);
            resolve();
          } else {
            const xml = xhr.responseText || '';
            const code = /<Code>([^<]+)<\/Code>/.exec(xml)?.[1] ?? '';
            reject(
              new Error(
                code === 'AccessDenied' && xml.includes('Policy expired')
                  ? '上传超时：文件太大 / 上行网速偏慢，凭证已过期。建议压缩后重试'
                  : `上传失败（HTTP ${xhr.status}${code ? ` ${code}` : ''}），请重试`,
              ),
            );
          }
        };
        xhr.onerror = () => reject(new Error('网络中断，上传未完成，请重试'));
        xhr.send(fd);
      });
    }
    return { url: sts.public_url };
  },

  // ─── Staff & Roles ───
  listStaff: (q: { search?: string; status?: 'active' | 'disabled'; role_id?: string; department?: string; page?: number; page_size?: number }) =>
    api.get<{ items: PlatformStaff[]; total: number }>('/admin/staff', { params: q }).then(r => r.data),
  getStaff: (id: string) =>
    api.get<PlatformStaff>(`/admin/staff/${id}`).then(r => r.data),
  createStaff: (data: { username: string; nickname: string; phone?: string; email?: string; department?: string; role_id: string }) =>
    api.post<CreateStaffResp>('/admin/staff', data).then(r => r.data),
  updateStaff: (id: string, data: Partial<{ nickname: string; phone: string; email: string; department: string; role_id: string; status: 'active' | 'disabled' }>) =>
    api.patch<PlatformStaff>(`/admin/staff/${id}`, data).then(r => r.data),
  resetStaffPassword: (id: string) =>
    api.post<ResetStaffPasswordResp>(`/admin/staff/${id}/reset-password`).then(r => r.data),

  listRoles: () =>
    api.get<(PlatformRole & { _count: { admins: number } })[]>('/admin/roles').then(r => r.data),
  getRole: (id: string) =>
    api.get<PlatformRole>(`/admin/roles/${id}`).then(r => r.data),
  // code 由后端自生成，前端不再传；保留 code? 仅作兼容
  createRole: (data: { name: string; description?: string; permissions: string[]; code?: string }) =>
    api.post<PlatformRole>('/admin/roles', data).then(r => r.data),
  updateRole: (id: string, data: Partial<{ name: string; description: string; permissions: string[] }>) =>
    api.patch<PlatformRole>(`/admin/roles/${id}`, data).then(r => r.data),
  // DELETE /admin/roles/:id —— 后端 PlatformRolesAdminController.remove；
  // 仅 !is_builtin 且 staff_count=0 可删，否则 400 builtin_role_immutable / role_in_use(带 staff_count)。
  deleteRole: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/roles/${id}`).then(r => r.data),

  // 后端 GET /admin/permissions 返回 { groups: PermissionGroup[] }（PlatformAuthController.listPermissions），
  // 解包 .groups 返回数组，保持调用方（RoleEditPage）拿到 PermissionGroup[]。
  listPermissions: () =>
    api.get<{ groups: PermissionGroup[] }>('/admin/permissions').then(r => r.data.groups),

  // ─── Audit / Login Logs ───
  listAdminAuditLogs: (q: { category?: AuditCategory; action?: string; user_id?: string; tenant_id?: string; resource?: string; start_at?: string; end_at?: string; search?: string; page?: number; page_size?: number }) =>
    api.get<{ items: AdminAuditLog[]; total: number; page: number; page_size: number }>('/admin/audit-logs', { params: q }).then(r => r.data),

  listAdminLoginLogs: (q: { actor_type?: LoginActorType; actor_username?: string; result?: LoginResult; ip?: string; start_at?: string; end_at?: string; page?: number; page_size?: number }) =>
    api.get<{ items: AdminLoginLog[]; total: number; page: number; page_size: number }>('/admin/login-logs', { params: q }).then(r => r.data),

  // Phase 7-D — Themes
  listThemes: () =>
    api.get<ThemeConfig[]>('/admin/themes').then(r => r.data),
  getTheme: (id: string) =>
    api.get<ThemeConfig>(`/admin/themes/${id}`).then(r => r.data),
  createTheme: (data: Partial<ThemeConfig> & { tokens: ThemeTokens }) =>
    api.post<ThemeConfig>('/admin/themes', data).then(r => r.data),
  updateTheme: (id: string, data: Partial<ThemeConfig> & { tokens?: ThemeTokens }) =>
    api.patch<ThemeConfig>(`/admin/themes/${id}`, data).then(r => r.data),
  deleteTheme: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/themes/${id}`).then(r => r.data),

  // RichEditor 图片直传 — 走 OSS putObject，后端 purpose=cms_image，5MB 上限
  uploadCmsImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads/cms-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  listAppVisits: (params: { start_date?: string; end_date?: string; page: number; page_size: number }) =>
    api.get<{ items: AppVisit[]; total: number; page: number; page_size: number }>('/admin/app-visits', { params }).then(r => r.data),
  listCardChannels: (params: CardChannelQuery) =>
    api.get<{items:CardChannelRow[];total:number;page:number;page_size:number}>('/admin/card-channels',{params}).then(r=>r.data),
  exportCardChannels: (params: CardChannelExportQuery): Promise<Blob> =>
    api.get<Blob>('/admin/card-channels/export',{params,responseType:'blob'}).then(r=>r.data),

  // 对公转账凭证直传 — 走 OSS，后端 purpose=bank_voucher（图片/PDF，5MB 上限）。
  // 替代原来 base64 内联进 JSON（会爆 body-parser → 500）。
  uploadVoucher: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=bank_voucher', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  // ─── 服务大厅（A 端运营维护 → M 端「服务大厅」tab 展示） ───
  // 列表一次性返回全量（条目量级小），分页由前端 clientPagination 处理，与促销/主题一致。
  listServiceHallItems: (params?: { created_from?: string; created_to?: string }) =>
    api.get<ServiceHallItem[]>('/admin/service-hall', { params }).then(r => r.data),
  getServiceHallItem: (id: string) =>
    api.get<ServiceHallItem>(`/admin/service-hall/${id}`).then(r => r.data),
  createServiceHallItem: (data: Partial<ServiceHallItem>) =>
    api.post<ServiceHallItem>('/admin/service-hall', data).then(r => r.data),
  updateServiceHallItem: (id: string, data: Partial<ServiceHallItem>) =>
    api.patch<ServiceHallItem>(`/admin/service-hall/${id}`, data).then(r => r.data),
  deleteServiceHallItem: (id: string) =>
    api.delete<ServiceHallItem>(`/admin/service-hall/${id}`).then(r => r.data),
  // 服务图标直传 — 后端 purpose=service_hall_icon（仅图片，5MB，落 OSS service-hall-icons/）
  uploadServiceHallIcon: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=service_hall_icon', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  // 服务详情页素材直传 — 后端 purpose=service_hall_page（text/html 或图片，5MB，落 OSS service-hall-pages/）
  // 返回的 url 直接填进条目的 link_url，M 端 web-view 打开
  uploadServiceHallPage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; key: string }>('/admin/uploads?purpose=service_hall_page', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};

export interface ThemeTokens {
  color: Record<string, string>;
  effect: Record<string, string>;
}

export interface ThemeConfig {
  id: string;
  name: string;
  description?: string | null;
  source: 'platform_default' | 'tenant_specific';
  target_tenant_id?: string | null;
  tokens: ThemeTokens;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppVisit {
  source_card?: {id:string;name:string}|null;
  source_owner?: {id:string;name:string}|null;
  id: string;
  visited_at: string;
  visitor_type: 'guest' | 'user';
  visitor_id: string;
  visitor_name: string | null;
  entry_page: string | null;
  source_scene: string | null;
  entry_tenant_id: string | null;
  entry_tenant_name: string | null;
}

export type ChannelSource='qr'|'friend'|'group'|'timeline'|'unknown';
export type CardChannelQuery={owner?:string;tenant?:string;visitor?:string;tenant_kind?:'personal'|'organization';source?:ChannelSource;start_date?:string;end_date?:string;page:number;page_size:number};
export type CardChannelExportQuery=Omit<CardChannelQuery,'page'|'page_size'>;
export type CardChannelRow={
  row_key:string;visitor:{type:'user'|'guest';id:string;name:string};card:{id:string;name:string};owner:{id:string;name:string};
  tenant:{id:string;name:string;kind:'personal'|'organization'};sources:ChannelSource[];visit_count:number;first_visit_at:string;last_visit_at:string;
};
